import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useAuth } from '@/features/auth/state/useAuth'
import type { UserRole } from '@/features/auth/model/contracts'

const ROLE_COLORS: Record<UserRole, string> = {
  admin: 'bg-primary/10 text-primary',
  editor: 'bg-accent/10 text-accent-foreground',
  viewer: 'bg-muted text-muted-foreground',
}

function RoleBadge({ role }: { role: UserRole }) {
  const { t } = useTranslation('settings')
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        ROLE_COLORS[role],
      )}
    >
      {t(`users.role.${role}`)}
    </span>
  )
}

export function SettingsUsersTab() {
  const { t } = useTranslation('settings')
  const { user } = useAuth()

  const inputClass = cn(
    'w-full rounded-md border border-border bg-input px-3 py-2 text-sm text-foreground',
    'placeholder:text-muted-foreground',
    'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  )

  const selectClass = cn(
    'rounded-md border border-border bg-input px-3 py-2 text-sm text-foreground',
    'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  )

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-foreground">
            {t('users.members')}
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                  {t('users.name')}
                </th>
                <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                  {t('users.email')}
                </th>
                <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                  {t('users.role')}
                </th>
              </tr>
            </thead>
            <tbody>
              {user && (
                <tr className="border-b border-border last:border-0">
                  <td className="px-6 py-4 font-medium text-foreground">
                    {user.fullName ?? user.username}
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">{user.email}</td>
                  <td className="px-6 py-4">
                    <RoleBadge role={user.role} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-foreground">{t('users.invite')}</h2>
        </div>

        <div className="px-6 py-6">
          <div className="flex gap-3">
            <input
              type="email"
              className={inputClass}
              placeholder={t('users.invitePlaceholder')}
              disabled
            />
            <select className={selectClass} disabled>
              <option value="viewer">{t('users.role.viewer')}</option>
              <option value="editor">{t('users.role.editor')}</option>
              <option value="admin">{t('users.role.admin')}</option>
            </select>
            <button
              type="button"
              disabled
              className={cn(
                'shrink-0 rounded-md border border-border bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              {t('users.invite')}
            </button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t('users.comingSoon')}</p>
        </div>
      </div>
    </div>
  )
}
